<template>
  <model-table
    :data="commutes"
    :noItemLabel="$t('compCarbonFormsCommutesTable.no-commute')"
    :module="module"
    :disabled="isSubmitted || isClonedSurvey"
    :singleUpdateDisabled="true"
    :bulkOpsDisabled="!authStore.isAuthenticated"
    :withTags="authStore.isAuthenticated"
    @update="$emit('update')"
    @edit="(item) => $emit('edit', item)"
    @del="(items) => $emit('del', items)"
  >
    <template slot="validity" slot-scope="props">
      <b-tag rounded v-if="props.props.row.cdeleted">{{
        $t('compCarbonFormsCommutesTable.state-1')
      }}</b-tag>
      <b-tag rounded v-else-if="props.props.row.isValid" type="is-success">{{
        $t('compCarbonFormsCommutesTable.state-2')
      }}</b-tag>
      <b-tag rounded v-else type="is-danger">{{
        $t('compCarbonFormsCommutesTable.state-3')
      }}</b-tag>
    </template>
    <b-table-column
      field="seqID"
      sortable
      :label="$t('id')"
      centered
      :custom-sort="sortById"
      numeric
      :td-attrs="maskedOnTest"
      searchable
      :custom-search="searchId"
    >
      <template #searchable="props">
        <b-select
          v-model="props.filters[props.column.field]"
          size="is-small"
          :placeholder="$t('Filter')"
        >
          <option value="all">
            {{ $t('compCarbonFormsCommutesTable.select.option1') }}
          </option>
          <option value="fromFile">
            {{ $t('compCarbonFormsCommutesTable.select.option2') }}
          </option>
          <option value="fromSurvey">
            {{ $t('compCarbonFormsCommutesTable.select.option3') }}
          </option>
        </b-select>
      </template>
      <template v-slot="props">
        <del v-if="props.row.cdeleted">{{ props.row.seqID }}</del>
        <span data-testId="maskedOnTest" v-else>{{ props.row.seqID }}</span>
      </template>
    </b-table-column>
    <b-table-column
      field="position"
      sortable
      :label="$t('position')"
      centered
      searchable
      :custom-search="searchPosition"
    >
      <template #searchable="props">
        <b-input
          v-model="props.filters[props.column.field]"
          size="is-small"
          icon="search"
          :placeholder="$t('Filter')"
        ></b-input>
      </template>
      <template v-slot="props">
        <del v-if="props.row.cdeleted">{{
          $t(props.row.position + '.short')
        }}</del>
        <span v-else>{{ $t(props.row.position + '.short') }}</span>
      </template>
    </b-table-column>
    <b-table-column
      field="nWorkingDay"
      sortable
      :label="$t('compCarbonFormsCommutesTable.field-2')"
      numeric
      v-slot="props"
    >
      <del v-if="props.row.cdeleted">{{ props.row.nWorkingDay }}</del>
      <span v-else>{{ props.row.nWorkingDay }}</span>
    </b-table-column>
    <b-table-column
      field="dailyDistance"
      sortable
      :custom-sort="sortByDailyDistance"
      :label="$t('compCarbonFormsCommutesTable.field-3')"
      numeric
      v-slot="props"
    >
      <del v-if="props.row.cdeleted">{{
        props.row.averageWeekDistance | splitNumber
      }}</del>
      <span v-else>{{ props.row.averageWeekDistance | splitNumber }}</span>
    </b-table-column>
    <b-table-column
      field="distanceTotale"
      sortable
      :custom-sort="sortByDistanceTotale"
      :label="$t('compCarbonFormsCommutesTable.field-4')"
      numeric
      v-slot="props"
    >
      <del v-if="props.row.cdeleted">{{
        props.row.getAnnualDistance(options, GHGIYear) | splitNumber
      }}</del>
      <span v-else>{{
        props.row.getAnnualDistance(options, GHGIYear) | splitNumber
      }}</span>
    </b-table-column>
    <template slot="delete" slot-scope="props">
      <b-tooltip :label="delLabel(props.props.row)" type="is-black">
        <b-button
          :disabled="isSubmitted || isClonedSurvey"
          size="is-small"
          :icon-right="delIcon(props.props.row)"
          @click="delFunction(props.props.row)"
          data-testId="maskedOnTest"
        />
      </b-tooltip>
    </template>
  </model-table>
</template>

<script>
import { useAuthenticationStore } from '@/stores/authentication.js'
import Modules from '@/models/Modules.js'
import { useCoreStore } from '@/stores/core.js'
import { useGHGIStore } from '@/stores/ghgi.js'

export default {
  name: 'Table',
  emits: ['update', 'del', 'view'],
  setup () {
    const authStore = useAuthenticationStore()
    const coreStore = useCoreStore()
    const ghgiStore = useGHGIStore()
    return { authStore, coreStore, ghgiStore }
  },
  components: {
    'model-table': () => import('@/components/carbon/forms/ModelTable.vue')
  },
  computed: {
    GHGIYear: function () {
      return this.ghgiStore.item.year
    },
    commutes: function () {
      return this.ghgiStore.items(Modules.COMMUTES)
    },
    options: function () {
      return this.coreStore.options
    },
    module: function () {
      return Modules.COMMUTES
    },
    isSubmitted: function () {
      return this.ghgiStore.item.isSubmitted
    },
    isClonedSurvey: function () {
      return this.ghgiStore.isClonedSurvey
    }
  },
  methods: {
    maskedOnTest (row, columns) {
      return { 'data-testid': 'maskedOnTest' }
    },
    getNum (id) {
      if (id.startsWith('s')) {
        return Number(id.slice(1))
      } else {
        return Number(id)
      }
    },
    sortById (a, b, isAsc) {
      const [idA, idB] = [a.seqID, b.seqID]
      if (idA.startsWith('s') !== idB.startsWith('s')) {
        return isAsc ? 1 : -1
      }
      const diff = this.getNum(idA) - this.getNum(idB)
      return isAsc ? diff : -diff
    },

    sortByDailyDistance (a, b, isAsc) {
      let da = a.averageWeekDistance
      let db = b.averageWeekDistance
      if (da < db) {
        return isAsc ? -1 : 1
      }
      if (da > db) {
        return isAsc ? 1 : -1
      }
      return 0
    },
    sortByDistanceTotale (a, b, isAsc) {
      let da = a.getAnnualDistance(this.options, this.GHGIYear)
      let db = b.getAnnualDistance(this.options, this.GHGIYear)
      if (da < db) {
        return isAsc ? -1 : 1
      }
      if (da > db) {
        return isAsc ? 1 : -1
      }
      return 0
    },
    display: function (commute) {
      this.$emit('view', commute)
    },
    delIcon: function (item) {
      let icon = 'trash'
      if (!this.isEditable && item.cdeleted) {
        icon = 'undo'
      }
      return icon
    },
    delLabel: function (item) {
      let label = this.$t('delete')
      if (!this.isEditable && !item.cdeleted) {
        label = this.$t('compCarbonFormsCommutesTable.tooltip-1')
      } else if (!this.isEditable && item.cdeleted) {
        label = this.$t('compCarbonFormsCommutesTable.tooltip-2')
      }
      return label
    },
    delFunction: function (item) {
      this.$emit('del', [item])
    },
    searchId: function (row, input) {
      if (input === 'fromFile') {
        return row.fromFile
      } else if (input === 'fromSurvey') {
        return !row.fromFile
      } else {
        return true
      }
    },
    searchPosition: function (row, input) {
      let position = this.$t(row.position + '.short').toLowerCase()
      return position.includes(input)
    }
  }
}
</script>
